import { Status } from '@atlassian/browser-storage-controls/controllers/cookie-controls/set-cookie/types';
import { getCookie } from '@atlassian/browser-storage-controls/controllers/cookie-controls/get-cookie';
import { deleteCookie } from '@atlassian/browser-storage-controls/controllers/cookie-controls/delete-cookie';
import { setStrictlyNecessaryCookie } from '@atlassian/browser-storage-controls/controllers/cookie-controls/set-strictly-necessary-cookie';
import {
	type JSCookieAttributes,
	setCookie,
} from '@atlassian/browser-storage-controls/controllers/cookie-controls/set-cookie';
import { checkThirdParty } from '@atlassian/browser-storage-controls/controllers/third-party-controls/check-third-party';

export enum CookieLevel {
	USE_PREFERENCES = 'USE_PREFERENCES',
	NECESSARY = 'NECESSARY',
}

// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
// to the customer test checks (which previously also matched internal test traffic including hello).
let hasSetAnalyticsCookie = false;

export class Cookies {
	// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
	// to the customer test checks (which previously also matched internal test traffic including hello).
	static hasAnalyticsCookieBeenSet(): boolean {
		return hasSetAnalyticsCookie;
	}

	/**
	 * Attempts to set a cookie asynchronously using the user's preferences
	 * @param level Describes how necessary the cookie is. Only use NECESSARY if the cookie is absolutely required.
	 * @returns true if the cookie succeeded in being set based on the user's preferences
	 */
	static async set(
		key: string,
		value: string,
		attributes?: JSCookieAttributes,
		level: CookieLevel = CookieLevel.USE_PREFERENCES,
	): Promise<boolean> {
		// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
		// to the customer test checks (which previously also matched internal test traffic including hello).
		if (key === 'atlassian_analytics_debug') {
			hasSetAnalyticsCookie = true;
		}

		// This does not need to be behind the FF because
		// if the cookie is strictly necessary, its set synchronously using js-cookie anyways
		if (level === CookieLevel.NECESSARY) {
			setStrictlyNecessaryCookie(key, value, attributes);
			return Promise.resolve(true);
		}

		return (await setCookie(key, value, attributes)) === Status.SUCCESS;
	}

	/**
	 * Retrieves a single cookie or all cookies if no key is provided
	 */
	// eslint-disable-next-line no-dupe-class-members
	static get(): { [key: string]: string };
	// eslint-disable-next-line no-dupe-class-members
	static get(key: string): string | undefined;
	// eslint-disable-next-line no-dupe-class-members
	static get(key?: string) {
		if (!key) {
			return getCookie();
		}
		return getCookie(key);
	}

	/**
	 * Deletes a cookie given a key
	 */
	static remove(key: string, attributes?: JSCookieAttributes): void {
		deleteCookie(key, attributes);
	}

	/**
	 * Executes a callback if the user has allowed or denied a third party cookie
	 */
	static async checkThirdParty(
		key: string,
		onAllowed: () => Promise<void>,
		onDenied?: () => Promise<void>,
	): Promise<void> {
		await checkThirdParty(key, onAllowed, onDenied);
	}
}
